import { ContentObjectConfig, ContentObjectInformation, ContentObjectWatcherInterface } from '@mbrtargeting/metatag-shared-types';
import { BehaviorSubject, Observable, createMutationObservable, debounce, getElementPosition, isHTMLElement, isSelectorValid, on } from '@mbrtargeting/metatag-utils';


const INITIAL_CONFIG: ContentObjectConfig = {
    contentWidthModifier: 0,
    contentTopModifier: 0,
    contentLeftModifier: 0,
    contentContainerQuery: undefined,
};

const INITIAL_INFORMATION: ContentObjectInformation = {
    element: null,
    width: 0,
    top: 0,
    left: 0,
    widthUnmodified: 0,
    topUnmodified: 0,
    leftUnmodified: 0,
};

/**
 * Check if two ContentObjectInformation objects contain the same information
 */
const isIdentical = (a: ContentObjectInformation, b: ContentObjectInformation): boolean => (
    a.element === b.element &&
    a.width === b.width &&
    a.top === b.top &&
    a.left === b.left &&
    a.widthUnmodified === b.widthUnmodified &&
    a.topUnmodified === b.topUnmodified &&
    a.leftUnmodified === b.leftUnmodified
)

export class ContentObjectWatcher implements ContentObjectWatcherInterface {

    #config$ = new BehaviorSubject<ContentObjectConfig>(INITIAL_CONFIG);
    #contentContainer$ = new BehaviorSubject<Element | null>(null);
    #contentObject$ = new BehaviorSubject<ContentObjectInformation>(INITIAL_INFORMATION);

    constructor() {
        const evaluateContentContainerQuery = ({ contentContainerQuery }: ContentObjectConfig): void => {
            const contentContainer = isSelectorValid(contentContainerQuery) ? document.querySelector(contentContainerQuery) : null;
            // only emit if the contentContainer has changed
            if (this.#contentContainer$.value !== contentContainer) {
                this.#contentContainer$.next(contentContainer);
            }
        }

        const evaluateContentObject = debounce((): void => {
            const element = this.#contentContainer$.value;
            const { contentWidthModifier = 0, contentTopModifier = 0, contentLeftModifier = 0 } = this.#config$.value;

            // if element is not found yet, OR if we have an element, but it was removed from the DOM, we need to re-evaluate the contentContainerQuery
            if (!element || !element.isConnected) {
                return evaluateContentContainerQuery(this.#config$.value);
            }

            const { width = 0, top = 0, left = 0 } = isHTMLElement(element) ? getElementPosition(element) : {};

            // calculate the contentObject
            const contentObject = {
                element,
                width: Math.round(width + contentWidthModifier),
                top: Math.round(top + contentTopModifier),
                left: Math.round(left + contentLeftModifier),
                widthUnmodified: Math.round(width),
                topUnmodified: Math.round(top),
                leftUnmodified: Math.round(left),
            };

            // only emit if the contentObject has changed
            if (!isIdentical(this.#contentObject$.value, contentObject)) {
                this.#contentObject$.next(contentObject);
            }
        }, 20);

        // whenever the settings change we need to update the contentContainer element
        this.#config$.subscribe(evaluateContentContainerQuery);

        // whenever the contentContaier element changes we need to update the contentObjectInformation
        this.#contentContainer$.subscribe(evaluateContentObject);

        // when something in the DOM changes we want to update the contentObjectInformation
        createMutationObservable(document, { childList: true, attributes: true, characterData: false, subtree: true }).subscribe(evaluateContentObject);

        // when the window resizes we want to update the contentObjectInformation
        on(window, 'resize').subscribe(evaluateContentObject);
    }

    public configure(config: ContentObjectConfig) {
        this.#config$.next(config);
    }

    get contentObject(): ContentObjectInformation {
        return this.#contentObject$.value;
    }

    /**
     * @deprecated use contentObject$ instead
     */
    get contentObjectPromise(): Promise<ContentObjectInformation> {
        return this.#contentObject$.find(i => !!i.element);
    }

    get contentObject$(): Observable<ContentObjectInformation> {
        // using map to return a new Observable instead of the BehaviorSubject itself
        return this.#contentObject$.map(i => i);
    }
}
